import { Component, OnInit } from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { ActivatedRoute, RouterModule } from "@angular/router";
import { debounceTime, forkJoin, Subject, Subscription, switchMap, tap } from "rxjs";
import { Question } from "src/app/classes/dynamic-form";
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { QuestionService } from "src/app/services/question.service";
import { LastSaveMsgComponent } from "../last-save-msg/last-save-msg.component";
import { DynamicFormEditorService } from "src/app/services/dynamic-form-editor.service";
import { QuestionBuilderBranchComponent } from "./question-builder-branch.component";


@Component( {
	standalone: true,
	selector: "dynamic-form-editor",
	templateUrl: "./dynamic-form-editor.component.html",
	styleUrl: "./dynamic-form-editor.component.scss",
	imports: [
		FormsModule, ReactiveFormsModule, MatInputModule, RouterModule, 
		MatProgressSpinnerModule, MatIconModule, MatFormFieldModule, MatButtonModule, 
		LastSaveMsgComponent,
		QuestionBuilderBranchComponent,
	],
	
})
export class DynamicFormEditorComponent implements OnInit {
	

	subscriptions = new Subscription();
	
	bSaving = false;
	bLoading = true;
	bLoadingError = false;

	formFormGroup = new FormGroup( {
		title: new FormControl<string>( "", Validators.required )
	});

	formTitle = "";
	formId = "";
	questionTree: Question[] = [];

	tmsLastSaveStart: number|null = null;
	tmsLastSaveEnd: number|null = null;
	formSaveNeeded = new Subject<boolean>();
	formSaveNeeded$ = this.formSaveNeeded.asObservable();
	bFormSaveError = false;
	bQuestionCreationInProgress = false;
	bQuestionCreationError = false;

	bLockedForm = false;

	
	constructor(
		private questionService: QuestionService,
		private activatedRoute: ActivatedRoute,
		private dynamicFormEditorService: DynamicFormEditorService
	) {}


	ngOnInit() {

		this.subscriptions.add(
			this.formFormGroup.valueChanges.subscribe( v => {

				this.formTitle = v.title || "";
				if( this.formTitle ) this.formSaveNeeded.next(true);

			})
		);

		this.subscriptions.add(
			this.formSaveNeeded$.pipe(
				debounceTime( 500 ),
				switchMap( b => {

					this.tmsLastSaveStart = (new Date()).getTime();
					this.bSaving = true;
					return this.questionService.saveForm( this.formTitle, this.formId ).pipe( 
						
						tap( formId => {

							this.bSaving = false;
							this.bFormSaveError = !formId;
							if( !formId ) {
								this.tmsLastSaveStart = null;
								this.tmsLastSaveEnd = null;
							}
							else {
								this.tmsLastSaveEnd = (new Date()).getTime();
								this.formId = formId;
							}

						})

					);
				} )
			).subscribe()
		);

		this.subscriptions.add(
			this.activatedRoute.params.subscribe( params => {

				this.bLoading = true;

				if( params['formId'] ) {

					this.formId = params[ 'formId' ];

					forkJoin( {
						form: this.questionService.getForm( this.formId ),
						isEditable: this.questionService.isFormDeletable( this.formId )
					}).subscribe( results => {

						this.bLoading = false;

						if( !results.form || (results.isEditable === null)) {
							this.bLoadingError = true;
							return;
						} 

						this.bLoadingError = false;

						this.formFormGroup.patchValue( { title: results.form.fkBezeichnung }, { emitEvent: false } );
						this.questionTree = this.dynamicFormEditorService.initialiseFormBuilder( this.formId, results.form.questions );

						if( !results.isEditable ) {
							for( const q of results.form.questions ) q.bIsDisabled = true;
							this.formFormGroup.disable( { emitEvent: false } );
							this.bLockedForm = true;
						}
						else {
							this.formFormGroup.enable( { emitEvent: false } );
							this.bLockedForm = false;
						}

					});

				}
				else {

					this.questionService.saveForm( " ", "" ).subscribe( formId => {

						this.bLoading = false;
						this.bFormSaveError = !formId;

						if( !formId ) return;

						this.bLockedForm = false;
						this.formId = formId;
						this.questionTree = this.dynamicFormEditorService.initialiseFormBuilder( this.formId );

					});


				}

			})
		);

	}

	ngOnDestroy() {
		
		this.subscriptions.unsubscribe();

	}


	clickAddQuestion() {

		this.bQuestionCreationInProgress = true;

		this.dynamicFormEditorService.generateQuestion().subscribe( id => {

			this.bQuestionCreationInProgress = false;
			this.bQuestionCreationError = !id;

		});

	}


}